import React, { useState, useEffect } from "react"
import { useAuth } from "../../contexts/AuthContext"
import { notificationPreferencesApi, parentNotificationSubscriptionsApi } from "../../lib/api"

// Generate all 24 hours
const NOTIFICATION_TIMES = Array.from({ length: 24 }, (_, i) => {
  const hour = i
  const value = `${hour.toString().padStart(2, "0")}:00`
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
  const ampm = hour < 12 ? "AM" : "PM"
  const label = `${displayHour}:00 ${ampm}`
  return { value, label }
})

const NotificationSettings = () => {
  const { isStudent, isParent } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState("")
  const [sendingTest, setSendingTest] = useState(false)
  const [testSent, setTestSent] = useState(false)

  // Notification preferences
  const [emailEnabled, setEmailEnabled] = useState(true)
  const [smsEnabled, setSmsEnabled] = useState(false)
  const [dueTodayEnabled, setDueTodayEnabled] = useState(true)
  const [pastDueEnabled, setPastDueEnabled] = useState(true)
  const [deadlineRemindersEnabled, setDeadlineRemindersEnabled] = useState(true)
  const [notificationTime, setNotificationTime] = useState("07:00")

  // Phone verification
  const [phoneNumber, setPhoneNumber] = useState("")
  const [phoneVerified, setPhoneVerified] = useState(false)
  const [verificationCode, setVerificationCode] = useState("")
  const [showVerification, setShowVerification] = useState(false)
  const [sendingCode, setSendingCode] = useState(false)
  const [verifying, setVerifying] = useState(false)

  // Parent subscriptions
  const [subscriptions, setSubscriptions] = useState([])

  useEffect(() => {
    fetchPreferences()
  }, [])

  const fetchPreferences = async () => {
    try {
      setLoading(true)
      const data = await notificationPreferencesApi.get()

      setEmailEnabled(data.notification_preference.email_enabled)
      setSmsEnabled(data.notification_preference.sms_enabled)
      setDueTodayEnabled(data.notification_preference.due_today_enabled)
      setPastDueEnabled(data.notification_preference.past_due_enabled)
      setDeadlineRemindersEnabled(data.notification_preference.deadline_reminders_enabled ?? true)
      setNotificationTime(data.user.notification_time || "07:00")
      setPhoneNumber(data.user.phone_number || "")
      setPhoneVerified(data.user.phone_verified)

      if (isParent) {
        const subsData = await parentNotificationSubscriptionsApi.list()
        setSubscriptions(subsData.subscriptions || [])
      }
    } catch (err) {
      console.error("Failed to fetch preferences:", err)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setError("")
    setSaving(true)
    setSaved(false)

    try {
      await notificationPreferencesApi.update({
        email_enabled: emailEnabled,
        sms_enabled: smsEnabled,
        due_today_enabled: dueTodayEnabled,
        past_due_enabled: pastDueEnabled,
        deadline_reminders_enabled: deadlineRemindersEnabled,
        notification_time: notificationTime,
      })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      setError(err.message || "Failed to save preferences")
    } finally {
      setSaving(false)
    }
  }

  const handleSendVerificationCode = async () => {
    if (!phoneNumber.trim()) {
      setError("Please enter a phone number")
      return
    }

    setSendingCode(true)
    setError("")

    try {
      await notificationPreferencesApi.updatePhone(phoneNumber)
      setShowVerification(true)
      setPhoneVerified(false)
    } catch (err) {
      setError(err.message || "Failed to send verification code")
    } finally {
      setSendingCode(false)
    }
  }

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      setError("Please enter the verification code")
      return
    }

    setVerifying(true)
    setError("")

    try {
      await notificationPreferencesApi.verifyPhone(verificationCode)
      setPhoneVerified(true)
      setShowVerification(false)
      setVerificationCode("")
    } catch (err) {
      setError(err.message || "Invalid verification code")
    } finally {
      setVerifying(false)
    }
  }

  const handleSubscriptionUpdate = async (studentId, updates) => {
    try {
      const data = await parentNotificationSubscriptionsApi.update(studentId, updates)
      setSubscriptions((prev) =>
        prev.map((sub) =>
          sub.student_id === studentId ? { ...sub, ...data } : sub
        )
      )
    } catch (err) {
      setError(err.message || "Failed to update subscription")
    }
  }

  const handleSendTest = async () => {
    setSendingTest(true)
    setError("")
    setTestSent(false)

    try {
      await notificationPreferencesApi.sendTest()
      setTestSent(true)
      setTimeout(() => setTestSent(false), 5000)
    } catch (err) {
      setError(err.message || "Failed to send test notifications")
    } finally {
      setSendingTest(false)
    }
  }

  if (loading) {
    return (
      <section className="bg-white rounded-xl shadow-sm border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </section>
    )
  }

  return (
    <section className="bg-white rounded-xl shadow-sm border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">Notification Settings</h2>
            <p className="text-sm text-gray-500">
              Configure how and when you receive assignment reminders
            </p>
          </div>
        </div>
        <button
          onClick={handleSendTest}
          disabled={sendingTest}
          className="px-3 py-1.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg border border-indigo-200 disabled:opacity-50 flex items-center gap-1.5"
        >
          {sendingTest ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              Test Now
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {testSent && (
        <div className="mb-4 rounded-lg bg-blue-50 p-3 text-sm text-blue-700 flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
          Test notifications sent! Check your email based on your enabled settings.
        </div>
      )}

      {saved && (
        <div className="mb-4 rounded-lg bg-green-50 p-3 text-sm text-green-700 flex items-center gap-2">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Settings saved successfully
        </div>
      )}

      <div className="space-y-6">
        {/* Daily Notification Time */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Daily Notification Time
          </label>
          <select
            value={notificationTime}
            onChange={(e) => setNotificationTime(e.target.value)}
            className="block w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          >
            {NOTIFICATION_TIMES.map((time) => (
              <option key={time.value} value={time.value}>
                {time.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            When to send daily assignment reminders (in your timezone)
          </p>
        </div>

        {/* Notification Types */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">Notification Types</h3>
          <div className="space-y-3">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={dueTodayEnabled}
                onChange={(e) => setDueTodayEnabled(e.target.checked)}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700">Due today reminders</span>
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={pastDueEnabled}
                onChange={(e) => setPastDueEnabled(e.target.checked)}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700">Overdue assignment alerts</span>
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={deadlineRemindersEnabled}
                onChange={(e) => setDeadlineRemindersEnabled(e.target.checked)}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <div>
                <span className="text-sm text-gray-700">Deadline reminders</span>
                <p className="text-xs text-gray-500">Get notified 3 hours, 1 hour, and 30 minutes before assignments are due</p>
              </div>
            </label>
          </div>
        </div>

        {/* Delivery Methods */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">Delivery Methods</h3>
          <div className="space-y-3">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={emailEnabled}
                onChange={(e) => setEmailEnabled(e.target.checked)}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700">Email notifications</span>
            </label>
{/* SMS disabled until 10DLC registration is complete
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={smsEnabled}
                onChange={(e) => setSmsEnabled(e.target.checked)}
                disabled={!phoneVerified}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 disabled:opacity-50"
              />
              <span className="text-sm text-gray-700">
                SMS notifications
                {!phoneVerified && <span className="text-gray-400 ml-1">(requires verified phone)</span>}
              </span>
            </label>
*/}
          </div>
        </div>

{/* SMS disabled until 10DLC registration is complete
        <div className="pt-4 border-t">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Phone Number for SMS</h3>
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="(555) 123-4567"
                className="block w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
              {phoneVerified && (
                <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Verified
                </p>
              )}
            </div>
            {!phoneVerified && (
              <button
                onClick={handleSendVerificationCode}
                disabled={sendingCode || !phoneNumber.trim()}
                className="px-3 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg disabled:opacity-50"
              >
                {sendingCode ? "Sending..." : "Verify"}
              </button>
            )}
          </div>

          {showVerification && !phoneVerified && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">
                Enter the verification code sent to your phone:
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="123456"
                  maxLength={6}
                  className="w-32 rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                <button
                  onClick={handleVerifyCode}
                  disabled={verifying || !verificationCode.trim()}
                  className="px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {verifying ? "Verifying..." : "Confirm"}
                </button>
              </div>
            </div>
          )}
        </div>
*/}

        {/* Parent Subscriptions */}
        {isParent && subscriptions.length > 0 && (
          <div className="pt-4 border-t">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Per-Student Notifications</h3>
            <div className="space-y-4">
              {subscriptions.map((sub) => (
                <div key={sub.student_id} className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900 mb-2">{sub.student_name}</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={sub.email_enabled}
                        onChange={(e) => handleSubscriptionUpdate(sub.student_id, { email_enabled: e.target.checked })}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                      />
                      <span className="text-gray-700">Email</span>
                    </label>
{/* SMS disabled until 10DLC registration is complete
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={sub.sms_enabled}
                        onChange={(e) => handleSubscriptionUpdate(sub.student_id, { sms_enabled: e.target.checked })}
                        disabled={!phoneVerified}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded disabled:opacity-50"
                      />
                      <span className="text-gray-700">SMS</span>
                    </label>
*/}
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={sub.due_today_enabled}
                        onChange={(e) => handleSubscriptionUpdate(sub.student_id, { due_today_enabled: e.target.checked })}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                      />
                      <span className="text-gray-700">Due today</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={sub.past_due_enabled}
                        onChange={(e) => handleSubscriptionUpdate(sub.student_id, { past_due_enabled: e.target.checked })}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                      />
                      <span className="text-gray-700">Past due</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="pt-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Notification Settings"}
          </button>
        </div>
      </div>
    </section>
  )
}

export default NotificationSettings
